import { apiRequest } from "./queryClient";
import { disconnectWebSocket } from "./websocket";

export interface AuthUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  level: string;
  avatar?: string;
  trialCompleted?: boolean;
  userType?: string;
  classCredits?: number;
  emailVerified?: boolean;
  googleId?: string | null;
  microsoftId?: string | null;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  username: string;
}

let currentUser: AuthUser | null = null;

const STORAGE_KEY = 'passport2fluency_user';

const loadUserFromStorage = (): AuthUser | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading user from storage:', error);
    localStorage.removeItem(STORAGE_KEY);
  }
  return null;
};

const saveUserToStorage = (user: AuthUser | null) => {
  try {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.error('Error saving user to storage:', error);
  }
};

export const getCurrentUser = (): AuthUser | null => {
  if (!currentUser) {
    currentUser = loadUserFromStorage();
  }
  return currentUser;
};

export const setCurrentUser = (user: AuthUser | null) => {
  currentUser = user;
  saveUserToStorage(user);
};

export const login = async (data: LoginData): Promise<AuthUser> => {
  const response = await apiRequest("POST", "/api/auth/login", data);
  const result = await response.json();

  if (result.user) {
    setCurrentUser(result.user);

    const { queryClient } = await import("@/lib/queryClient");
    queryClient.clear();

    return result.user;
  }

  throw new Error("Login failed");
};

export const register = async (data: RegisterData): Promise<AuthUser> => {
  const res = await fetch("/api/auth/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
    credentials: "include",
  });

  const result = await res.json();

  if (!res.ok) {
    throw new Error(result.message || "Registration failed");
  }

  if (result.user) {
    setCurrentUser(result.user);

    const { queryClient } = await import("@/lib/queryClient");
    queryClient.clear();

    return result.user;
  }

  throw new Error("Registration failed");
};

export const logout = async () => {
  try {
    await apiRequest("POST", "/api/auth/logout");
  } catch {
    // Continue with local logout even if server call fails
  }
  disconnectWebSocket();
  setCurrentUser(null);
};

/**
 * Refresh the cached user (localStorage + module singleton) from /api/auth/me
 * and invalidate the React Query caches that depend on classCredits.
 * Call after any mutation that changes the user's credit balance (book, cancel, refund).
 */
export const refreshUserAndCredits = async (
  queryClient: { invalidateQueries: (opts: { queryKey: any[] }) => void },
  userId?: number,
): Promise<void> => {
  try {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (res.ok) {
      const data = await res.json();
      if (data.user) setCurrentUser(data.user);
    }
  } catch {
    // Invalidations below will trigger a refetch even if this fetch failed
  }
  queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
  if (userId !== undefined) {
    queryClient.invalidateQueries({ queryKey: ["/api/dashboard", userId] });
    queryClient.invalidateQueries({ queryKey: ["/api/classes", userId] });
  }
  queryClient.invalidateQueries({ queryKey: ["/api/classes/pending-confirmation"] });
};

export const validateSession = async (): Promise<AuthUser | null> => {
  try {
    const response = await fetch("/api/auth/me", { credentials: "include" });
    if (response.ok) {
      const result = await response.json();
      if (result.user) {
        setCurrentUser(result.user);
        return result.user;
      }
    }
  } catch {
    // Session invalid
  }
  setCurrentUser(null);
  return null;
};

export const isAuthenticated = (): boolean => {
  return getCurrentUser() !== null;
};

export const getSmartRedirect = (user: AuthUser | null): string => {
  if (!user) return "/login";

  switch (user.userType) {
    case "admin":
      return "/admin";
    case "tutor":
      return "/tutor-portal";
    case "customer":
      return "/dashboard";
    case "lead":
      return "/packages";
    case "trial":
    default:
      if (user.trialCompleted) return "/packages";
      return "/home";
  }
};
